import React from 'react';
import type { TikTokPlayerApi } from './useTikTokPlayerApi.types';
import { isTikTokMessage } from './useTikTokPlayerApi.utils';

type UseTikTokPlayerApiProps = {
  startTime?: number;
  endTime?: number;
  onPlayerReady: (api: TikTokPlayerApi) => void;
};

const constrainPlaybackTime = ({
  currentTime,
  playerApi,
  startTime,
  endTime,
}: {
  playerApi: TikTokPlayerApi;
  startTime?: number;
  endTime?: number;
  currentTime: number;
  duration: number;
}) => {
  if (startTime && currentTime < startTime) {
    playerApi.seekTo(startTime);
  }
  if (endTime && currentTime >= endTime) {
    playerApi.seekTo(endTime);
    playerApi.pause();
  }
};

export const useTikTokPlayerApi = ({
  startTime,
  endTime,
  onPlayerReady,
}: UseTikTokPlayerApiProps) => {
  const playerRef = React.useRef<HTMLIFrameElement>(null);
  const playerApi: TikTokPlayerApi = React.useMemo(() => {
    const getPlayerOrigin = (el: HTMLIFrameElement) => new URL(el.src).origin;

    return {
      mute: () => {
        if (!playerRef.current) {
          return;
        }
        playerRef.current.contentWindow?.postMessage(
          { type: 'mute', 'x-tiktok-player': true },
          getPlayerOrigin(playerRef.current),
        );
      },
      pause: () => {
        if (!playerRef?.current) {
          return;
        }
        playerRef.current.contentWindow?.postMessage(
          { type: 'pause', 'x-tiktok-player': true },
          getPlayerOrigin(playerRef.current),
        );
      },
      seekTo: (time: number) => {
        if (!playerRef?.current) {
          return;
        }
        playerRef.current.contentWindow?.postMessage(
          { type: 'seekTo', 'x-tiktok-player': true, value: time },
          getPlayerOrigin(playerRef.current),
        );
      },
    };
  }, []);

  const handleTikTokEvent = React.useCallback(
    (message: MessageEvent) => {
      if (
        playerRef.current &&
        message.source === playerRef.current?.contentWindow &&
        isTikTokMessage(message)
      ) {
        const type = message.data.type;

        switch (type) {
          case 'onPlayerReady': {
            return onPlayerReady?.(playerApi);
          }
          case 'onCurrentTime':
            const { duration, currentTime } = message.data.value;
            return constrainPlaybackTime({
              playerApi,
              currentTime,
              duration,
              startTime,
              endTime,
            });
          default:
            return type satisfies never;
        }
      }
    },
    [onPlayerReady, playerApi, startTime, endTime],
  );

  React.useEffect(() => {
    window.addEventListener('message', handleTikTokEvent, { passive: true });
    return () => {
      window.removeEventListener('message', handleTikTokEvent);
    };
  }, [handleTikTokEvent]);

  return { setPlayerRef: playerRef };
};
